/**
 * The invite page (/invite/<token>): public preview ("Ada invited you to Acme"), then — signed in — join.
 * Sign-in itself is SessionViewModel's; the token is kept there so it survives sign-in and OAuth redirects.
 */
import type { InvitePreviewResponse } from "@bundle/contracts";
import type { WorkspaceRef } from "../logic/onboarding.ts";
import { errorKind, ApiFailure, type ApiClient } from "../ports.ts";
import type { SessionViewModel } from "./SessionViewModel.ts";
import { ViewModel } from "./ViewModel.ts";

export type InviteError = "network" | "emailMismatch" | "invalid" | "failed";

export type InviteState = {
  token: string;
  /** "invalid": unknown, expired, used or revoked (the server doesn't say which). */
  status: "loading" | "ready" | "invalid" | "error";
  preview?: InvitePreviewResponse;
  joining: boolean;
  error?: InviteError;
};

export class InviteViewModel extends ViewModel<InviteState> {
  private readonly api: ApiClient;
  private readonly session: SessionViewModel;

  constructor(api: ApiClient, session: SessionViewModel, token: string) {
    super({ token, status: "loading", joining: false });
    this.api = api;
    this.session = session;
  }

  async load(): Promise<void> {
    this.update({ status: "loading" });
    try {
      const preview = await this.api.invitePreview(this.state.token);
      this.update({ status: "ready", preview });
    } catch (e) {
      const kind = errorKind(e);
      this.update({ status: kind === "notFound" || kind === "invalid" ? "invalid" : "error" });
    }
  }

  /** Joins (signed in). Resolves with the workspace to open, or undefined on failure (see `error`). */
  async join(): Promise<WorkspaceRef | undefined> {
    if (this.state.joining) return undefined;
    this.patch({ joining: true });
    try {
      const ws = await this.session.acceptInvite();
      this.patch({ joining: false });
      return ws;
    } catch (e) {
      const kind = errorKind(e);
      const code = e instanceof ApiFailure ? e.code : "";
      const error: InviteError =
        kind === "network" ? "network" : code === "invite_email_mismatch" ? "emailMismatch" : kind === "notFound" ? "invalid" : "failed";
      this.patch({ joining: false, error });
      if (error === "invalid") this.update({ status: "invalid" });
      return undefined;
    }
  }

  /** Leave the invite page (back to the app or sign-in). */
  dismiss(): void {
    this.session.setInviteToken(undefined);
  }

  private patch(p: Partial<InviteState>): void {
    const next: InviteState = { ...this.state, ...p };
    if (!("error" in p)) delete next.error;
    this.setState(next);
  }
}
